import React from "react";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { DEFAULT_ADMIN, User } from "../../model/User";
import { Box, IconButton, Tooltip } from "@mui/material";
import Icon from "../../components/ui/Icon";

type IsActionDisabled = (username: string) => boolean;
type HandleEditUser = (user: User) => void;
type HandleDeleteUser = (userId: number) => void;

export const getUserHiddenColumns = (): string[] => ["createdBy"];

export const getUserColumns = (
  isEditActionDisabled: IsActionDisabled,
  isDeleteActionDisabled: IsActionDisabled,
  handleEditUser: HandleEditUser,
  handleDeleteUser: HandleDeleteUser
): GridColDef[] => [
  {
    field: "username",
    headerName: "Username",
    flex: 1,
    hideable: false,
    maxWidth: 250,
  },
  {
    field: "email",
    headerName: "Email",
    flex: 1,
    hideable: false,
    minWidth: 100,
    maxWidth: 300,
  },
  {
    field: "roles",
    headerName: "Roles",
    flex: 1,
    hideable: false,
    width: 250,
    maxWidth: 500,
    valueGetter: (params: GridRenderCellParams) => {
      return Array.isArray(params) ? params.join(", ") : String(params);
    },
    renderCell: (params: GridRenderCellParams) => {
      const { value } = params;
      if (Array.isArray(value)) {
        return (
          <div className="font-display font-bold">
            {value.map((role: string, index: number) => (
              <span key={index}>
                {role}
                {index < value.length - 1 && ", "}
              </span>
            ))}
          </div>
        );
      }
      return <span className="font-display font-semibold">{value}</span>;
    },
  },
  {
    field: "createdAt",
    headerName: "Created At",
    flex: 1,
    maxWidth: 200,
  },
  {
    field: "createdBy",
    headerName: "Created By",
    flex: 1,
    maxWidth: 200,
  },
  {
    field: "actions",
    headerName: "Actions",
    sortable: false,
    filterable: false,
    hideable: false,
    renderCell: (params: GridRenderCellParams<User>) => {
      const { row } = params;
      const editDisabled = isEditActionDisabled(row.username || "");
      const deleteDisabled = isDeleteActionDisabled(row.username || "") || row.canDelete;

      return (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title={editDisabled ? "Cannot edit this user" : "Edit user"}>
            <span>
              <IconButton size="small" onClick={() => handleEditUser(row)} disabled={editDisabled} color="primary">
                <Icon icon="modify" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip
            title={
              row.canDelete && row.id !== DEFAULT_ADMIN.id
                ? "Cannot delete this user because he's referenced by other entities"
                : deleteDisabled
                ? "Cannot delete the user"
                : "Delete user"
            }
          >
            <span>
              <IconButton
                size="small"
                onClick={() => row.id && handleDeleteUser(row.id)}
                disabled={deleteDisabled}
                color="error"
              >
                <Icon icon="delete" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      );
    },
  },
];
